import React, { useEffect, useState, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { useWallet } from 'use-wallet'
import { Helmet } from 'react-helmet'
import { ToastContainer, toast } from 'react-toastify'
import Spinner from 'react-svg-spinner'
import 'react-toastify/dist/ReactToastify.css'
import { toUtf8 } from 'web3-utils'

import PageHeader from 'components/PageHeader'
import Spacer from 'components/Spacer'
import Button from 'components/Button'
import Input from 'components/Input'
import Label from 'components/Label'
import ApproveTransferModal from 'components/ApproveTransferModal'
import WalletProviderModal from 'components/WalletProviderModal'
import CreateNewPoolModal from 'components/CreateNewPoolModal'
import WalletUnlockButton from 'components/WalletUnlockButton'
import Tooltip from 'components/Tooltip'

import { AccountContext } from 'contexts/AccountProvider'
import { weiToEth, viewTranscationURL } from 'lottery/constants'
import { PoolListContext } from 'contexts/PoolListProvider'
import {
  getPoolRewardText,
  getTokenMetaFromAddress,
  getPoolTokenSymbol,
  getPoolTokenLogoURL,
  getTotalBonusTickets,
  getDrawBonus,
} from 'lottery/util'
import useAllowance from 'hooks/useAllowance'
import useModal from 'hooks/useModal'

const Pool = () => {
  const { poolId } = useParams()
  const { account } = useWallet()
  const { poolList } = useContext(PoolListContext)
  const allowance = useAllowance()
  const history = useHistory()
  const { poolsContract, getAllowance } = useContext(AccountContext)
  const [drawBonus, setDrawBonus] = useState(0)
  const [tickets, setTickets] = useState(1)
  const [isPurchasable, setIsPurchasable] = useState(true)
  const [errMessage, setErrMessage] = useState('')
  const [isDrawable, setIsDrawable] = useState(false)
  const [selectedPool, setSelectedPool] = useState(null)
  const [loading, setLoading] = useState(false)
  const [renewExpireDate, setRenewExpireDate] = useState()

  let hashLink = ''

  const [onPresentWalletProviderModal] = useModal(<WalletProviderModal />)
  const [onPresetApproveTranferModal] = useModal(
    <ApproveTransferModal
      ERC20TokenAddress={selectedPool ? selectedPool.token : null}
    />
  )

  const [onPresentApproveTransferModal] = useModal(<ApproveTransferModal />)
  const [onPresentCreateNewPoolModal] = useModal(
    <CreateNewPoolModal history={history} />
  )
 
  let poolData = poolList.find(item => item.pid == poolId)
  var expireDate = new Date(poolData?.expiry*1000)
  const currentDate = new Date()

  var res = (expireDate - currentDate) / 1000
  var restExpireDays = Math.floor(res / 86400)

 

  useEffect(() => {
    if (poolsContract && selectedPool) {
      getDrawBonus(selectedPool, poolsContract).then((result) => {
        setDrawBonus(result)
      })
    }
  }, [poolsContract, selectedPool])

  useEffect(() => {
    if (poolsContract) {
      getContracts()
      setRenewExpireDate(parseInt(poolData?.expiry) + parseInt(2592000))
    }
  }, [poolsContract, account])

  const getLatestName = async (lotteryPool, pid) => {
    const nameEvents = await lotteryPool.getPastEvents('PoolNameChange', {
      filter: {
        poolId: pid.toString(),
      },
      fromBlock: 1,
      toBlock: 'latest',
    })
    let nameEvent = await nameEvents.pop()
    let name = 'you bet!'
    let transactionHash = ''
    if (nameEvent !== undefined) {
      name = toUtf8(nameEvent.returnValues.name)
      transactionHash = nameEvent.transactionHash
    }
    return { name, transactionHash }
  }

  const getContracts = async () => {
    const pool = await poolsContract.methods.pools(poolId).call()
    const {
      token,
      balance,
      reward,
      weight,
      owner,
      size,
      tickets,
      weightBalance,
      expiry,
      status,
    } = pool

    const { name, transactionHash } = await getLatestName(poolsContract, poolId)
    const lotteryPool = {
      pid: poolId,
      token,
      balance,
      reward,
      weight,
      owner,
      size,
      tickets,
      weightBalance,
      expiry,
      status,
      name,
      transactionHash,
    }
    
    if (lotteryPool) {
      if (lotteryPool.name === undefined || lotteryPool.name === '') {
        lotteryPool.name = 'You Bet!'
      }
      setSelectedPool(lotteryPool)
      if (lotteryPool.status !== '1') setIsPurchasable(false)
      const isDrawableTmp =
        lotteryPool.status === '1' &&
        lotteryPool.balance / lotteryPool.reward >= 1.2
      setIsDrawable(isDrawableTmp)
      if (poolsContract) {
        getContractConfig()
      }
    }
  }

  const getContractConfig = async () => {
    const poolThreshold = await poolsContract.methods.poolThreshold().call()
    const contract = await poolsContract.methods.pools(poolId).call()
    const isDrawable =
      contract.balance / contract.reward >= poolThreshold / 100000
    setIsDrawable(isDrawable)
  }

  const handleDraw = () => {
    poolsContract.methods
      .draw(BigNumber(poolId).toFixed(0))
      .send({ from: account })
      .on('transactionHash', (tx) => {
        return tx.transactionHash
      })
  }

  const transectionSuccess = () => {
    return (
      <div>
        Your purchase transaction went through successfully!{' '}
        <a href={hashLink} rel="noreferrer" target="_blank">
          View Transaction
        </a>
      </div>
    )
  }

  const handleRenew = async () => {
    poolsContract.methods
      .renewPool(poolId,
        selectedPool.weight,
        renewExpireDate
      )
      .send({ from: account })
      .on('transactionHash', (tx) => {
        getContracts()
    /*  history.push(`/`)
        history.push(`/pools/${poolId}`)
     */
        hashLink = viewTranscationURL + tx
        return tx
      })
      .on('confirmation', (confirmationNumber, receipt) => {
        if (confirmationNumber === 2 && receipt.status === true) {
          toast(transectionSuccess)
          setLoading(false)
        }
      })
    }

  const handlePurchase = async () => {
    if (!account) {
      onPresentWalletProviderModal()
      return
    }

    const allowance = await getAllowance(selectedPool.token)
    const cost =
      (tickets * weiToEth(BigNumber(selectedPool.reward))) /
      BigNumber(selectedPool.tickets)

    if (!allowance || weiToEth(BigNumber(allowance)) < cost) {
      onPresetApproveTranferModal()
      return
    }

    setIsPurchasable(false)
    /*   if (Number(tickets) > 20) {
      setTickets(1)
      setErrMessage('The tickets should be less than 20.')
      setIsPurchasable(true)
      return
    } */

    if (Number(tickets) == 0) {
      setTickets(1)
      setErrMessage('The minimum amount of tickets you can purchase is 1')
      setIsPurchasable(true)
      return
    }

    setLoading(true)
    poolsContract.methods
      .buyLottery(BigNumber(poolId).toFixed(0), BigNumber(tickets).toFixed(0))
      .send({ from: account })
      .on('transactionHash', (tx) => {
        hashLink = viewTranscationURL + tx
        return tx
      })
      .on('confirmation', (confirmationNumber, receipt) => {
        if (confirmationNumber === 2 && receipt.status === true) {
          toast(transectionSuccess)
          setLoading(false)
        }
      })

    setIsPurchasable(true)
  }

  const handleChangeTickets = (e) => {
    setErrMessage('')
    setTickets(e.target.value)
  }

  const handleGoHome = () => {
    history.push('/')
  }

  const handleCreateNewPool = async () => {
    if (!account) {
      onPresentWalletProviderModal()
      return
    }

    if (!allowance.toNumber()) {
      onPresentApproveTransferModal()
      return
    }

    onPresentCreateNewPoolModal()
    return
  }

  if (!account) {
    return (
      <>
        <WalletUnlockButton handleUnlockClick={handleCreateNewPool} />
      </>
    )
  }

  if (!selectedPool) {
    return (
      <>
        <StyledInfo>There is no Contract.</StyledInfo>
      </>
    )
  }

  return (
    <div>
      <Helmet>
        <title>You Bet! Enter to win {getPoolRewardText(selectedPool)}</title>
        <meta
          name="description"
          content={`You Bet! Enter to win ${getPoolRewardText(selectedPool)}`}
        />

        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/** Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`You Bet! Enter to win ${getPoolRewardText(selectedPool)}`}
        />
        <meta
          property="og:description"
          content={`You Bet! Enter to win ${getPoolRewardText(selectedPool)}`}
        />
        <meta property="og:image" content={getPoolTokenLogoURL(selectedPool)} />
        <meta
          property="og:image:secure_url"
          content={getPoolTokenLogoURL(selectedPool)}
        />
        <meta property="og:image:alt" content="Token Logo" />

        {/** Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta
          property="twitter:title"
          content={`You Bet! Enter to win ${getPoolRewardText(selectedPool)}`}
        />
        <meta
          property="twitter:description"
          content={`You Bet! Enter to win ${getPoolRewardText(selectedPool)}`}
        />
        <meta
          property="twitter:image"
          content={getPoolTokenLogoURL(selectedPool)}
        />
      </Helmet>
      <PageHeader
        icon={
          <img
            src={getPoolTokenLogoURL(selectedPool)}
            onClick={handleGoHome}
            height="120"
            alt="dice"
          />
        }
        title={selectedPool.pid == 0 ? `You Bet! Demo` : selectedPool.name}
        subtitle={
          selectedPool.pid == 0
            ? `This is a demonstration, not a raffle.
          A random selected winner will get ${getPoolRewardText(
            selectedPool
          )} reward.
          While we are a raffle building service and marketplace, we do not run our own raffles. `
            : `Enter to win ${getPoolRewardText(selectedPool)}`
        }
      />
      <StyledPool>
        <Input
          startAdornment="Purchase"
          endAdornment={`tickets at ${
            weiToEth(BigNumber(selectedPool.reward), getTokenMetaFromAddress(selectedPool.token).decimals) /
            Number(selectedPool.tickets)
          } ${getPoolTokenSymbol(selectedPool)} each`}
          placeholder="Amount"
          value={tickets}
          onChange={handleChangeTickets}
        />
        <Spacer />
        {errMessage && (
          <div>
            <StyledErrMessage>{errMessage}</StyledErrMessage>
            <Spacer />
          </div>
        )}
        <Label
          text={`Total cost: ${
            (tickets * weiToEth(BigNumber(selectedPool.reward), getTokenMetaFromAddress(selectedPool.token).decimals)) /
            BigNumber(selectedPool.tickets)
          } ${getPoolTokenSymbol(selectedPool)}  `}
        />
        <Label
          text={`Total bonus: ${getTotalBonusTickets(
            selectedPool,
            tickets
          )} UBET`}
        />
        <Spacer />
        {loading ? (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <Spinner />
            <Label size="12" text="Waiting for the transaction" />
            <Spacer />
          </div>
        ) : (
          <></>
        )}

        {(restExpireDays > 0) ? (
          <>
          {(restExpireDays >= 0 && restExpireDays < 7) ? (
            <>
              <Label text={`About to expire in ${restExpireDays} days`} />
              <Spacer size="md" />
              <StyledCardsWrapper>
                  <Button
                      text="Purchase"
                      disabled={!isPurchasable}
                      onClick={handlePurchase}
                    />   
                    <Button
                      text="Draw for Winner"
                      disabled={!isDrawable}
                      onClick={handleDraw}
                    />
              </StyledCardsWrapper>
            </>
          ) : (<>
                <StyledCardsWrapper>
                  <Button
                    text="Purchase"
                    disabled={!isPurchasable}
                    onClick={handlePurchase}
                  />   
                  <Button
                    text="Draw for Winner"
                    disabled={!isDrawable}
                    onClick={handleDraw}
                  />
                </StyledCardsWrapper>
              </>
          )}
          </>
        ):<>
            <Label text='Sorry, the pool expired' />
            <Spacer size="md" />
            <StyledCardsWrapper>
              <Button
                text="Renew Pool"
                disabled={!isPurchasable}
                onClick={handleRenew}
              />
            </StyledCardsWrapper>
      </>
        }

        {isDrawable && (
          <>
            <Spacer />
            <Label text={`Drawing bonus: ${drawBonus} UBET`} />
            <Tooltip
              text={`if you are the first one to draw this <br/>raffle pool, you'll get xx UBET bonus`}
            />
          </>
        )}
        <Spacer size="lg" />
        <Spacer size="lg" />
        <StyledInfo>
          ⭐️ Every time you purchase tickets or draw the raffle, you&lsquo;ll
          get UBET rewards!
        </StyledInfo>
        <Spacer size="lg" />
      </StyledPool>
      <ToastContainer />
    </div>
  )
}

const StyledPool = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 700px;
  margin: auto;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const StyledButtonWrapper = styled.span`
  margin-right: 20px;
  width: 400px;
`

const StyledCardsWrapper = styled.span`
  display: flex;
  width: 600px;
  @media (max-width: 768px) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: center;
  }
`

const StyledInfo = styled.h3`
  color: ${(props) => props.theme.color.grey[400]};
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  text-align: center;
`

const StyledErrMessage = styled.h3`
  color: ${(props) => props.theme.color.primary.main};
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  text-align: left;
`

export default Pool
